import type { DiscreteStop } from 'api-validations/stitch'

export const discreteStopId = (stop: { label?: string; value?: number }) =>
  stop.label ?? String(stop.value)

/**
 * A discrete legend stop in the unified legend, which merges all streams' discrete
 * values into a single flat list for display. Each stop carries the canonical color
 * and an identity key, plus a map of which streams have this value and what blue
 * channel value they use for it in their tile pixels.
 */
export interface ReconciledDiscreteStop {
  /**
   * The identity key for this stop: `stop.label` if present, otherwise `String(stop.value)`.
   * Used for grouping, deduplication, and highlight matching. This is NOT necessarily
   * a display-ready string — consumers should apply locale formatting when `value` is
   * present (i.e., when the backend provided a numeric value rather than a text label).
   */
  id: string
  /**
   * Raw numeric value from the API, present when the stop has no pre-formatted label.
   * The consuming hook is responsible for formatting this for display (unit conversion,
   * locale formatting). When absent, `id` is already display-ready.
   */
  value?: number
  color: string
  /**
   * Per-stream blue channel values for this label. Used to resolve highlighting:
   * when the user selects this label, each stream highlights the pixels that
   * correspond to its own blue channel value for this label.
   */
  instances: Map<string, number>
}

/**
 * The output of reconciling discrete legends across multiple streams.
 */
export interface ReconcileResult {
  /**
   * Whether any two streams share a blue channel value that maps to different labels.
   * When false, merging is safe with no remapping required.
   */
  hasConflicts: boolean
  /**
   * Per-stream remapped DiscreteStop arrays. The blue channel values are unchanged
   * (they are baked into tile pixels and cannot be modified), but the colors are
   * reassigned so that the same label always has the same color across all streams.
   *
   * The primary stream's color assignments are canonical — values that appear in
   * the primary keep their original colors. Secondary-stream-only values are
   * assigned colors from the supplemental palette.
   */
  remappedColors: Map<string, DiscreteStop[]>
  /**
   * Flat union of all discrete values across all streams, with canonical colors
   * and the per-stream instance map. Ordered by first appearance (primary stream
   * first, then secondary streams in order).
   */
  reconciledStops: ReconciledDiscreteStop[]
}

/**
 * A supplemental palette of visually distinct colors used when a secondary stream
 * introduces a label not present in the primary stream, and a new color must be
 * assigned to avoid overlap with existing assignments.
 *
 * These colors match the backend's discrete palette so that supplemental assignments
 * look natural alongside backend-assigned colors. Pure red and green are avoided
 * because they carry good/bad semantics elsewhere in the UI.
 */
const SUPPLEMENTAL_PALETTE: readonly string[] = [
  '#cc85ff',
  '#ff80aa',
  '#7581ff',
  '#c38d69',
  '#e9c7ff',
  '#c48292',
  '#b3e0ee',
  '#ffcad4',
  '#c6ba3f',
  '#d6b9a4',
  '#dc35eb',
  '#ffbe8f',
  '#b6cbb4',
  '#44aba6',
  '#f58231', // orange
  '#42d4f4', // cyan
  '#ffe119', // yellow
  '#4363d8', // blue
  '#808000', // olive
  '#000075' // navy
]

/**
 * Reconcile discrete legends across multiple streams into a unified legend.
 *
 * The primary stream (first entry) is canonical — its color assignments are preserved
 * unchanged. Secondary streams are remapped so that:
 *   - Labels that match the primary get the primary's color
 *   - Labels not in the primary get a new color from the supplemental palette
 *
 * Blue channel values in the tile pixels are never modified — only the color that
 * the shader maps each blue value to is changed.
 */
export const reconcileDiscreteLegends = (
  streams: Array<{ streamId: string; discreteColors: DiscreteStop[] }>
): ReconcileResult => {
  if (streams.length === 0) {
    return { hasConflicts: false, remappedColors: new Map(), reconciledStops: [] }
  }

  const idToColor = new Map<string, string>()
  const idToValue = new Map<string, number>()
  const usedColors = new Set<string>()
  const blueToId = new Map<number, string>()
  let hasConflicts = false
  let supplementalIndex = 0

  // Build the canonical id→color mapping. Primary stream's assignments win.
  // If a secondary stream introduces an id whose natural color is already taken
  // by a different id, assign it a supplemental color instead.
  // Also detect conflicts: any two streams sharing a blue value with different ids.
  for (const { discreteColors } of streams) {
    for (const stop of discreteColors) {
      const id = discreteStopId(stop)

      const existingBlueId = blueToId.get(stop.blue)
      if (existingBlueId === undefined) {
        blueToId.set(stop.blue, id)
      } else if (existingBlueId !== id) {
        hasConflicts = true
      }

      if (idToColor.has(id)) continue

      // Preserve the raw numeric value for consumer formatting (unit conversion, locale).
      // Labeled stops don't need this — their id is already the display string.
      if (stop.label === undefined && stop.value !== undefined) {
        idToValue.set(id, stop.value)
      }

      if (usedColors.has(stop.color)) {
        const newColor =
          SUPPLEMENTAL_PALETTE.find((c) => !usedColors.has(c)) ??
          SUPPLEMENTAL_PALETTE[supplementalIndex++ % SUPPLEMENTAL_PALETTE.length]
        idToColor.set(id, newColor)
        usedColors.add(newColor)
      } else {
        idToColor.set(id, stop.color)
        usedColors.add(stop.color)
      }
    }
  }

  // Build remapped DiscreteStop[] for each stream (blue values unchanged, colors canonical)
  const remappedColors = new Map<string, DiscreteStop[]>()
  for (const { streamId, discreteColors } of streams) {
    remappedColors.set(
      streamId,
      discreteColors.map((stop) => {
        const id = discreteStopId(stop)
        const canonicalColor = idToColor.get(id) ?? stop.color
        return { ...stop, color: canonicalColor }
      })
    )
  }

  // Build the unified legend with instances map (id → { streamId → blue })
  const instancesById = new Map<string, Map<string, number>>()
  for (const { streamId, discreteColors } of streams) {
    for (const stop of discreteColors) {
      const id = discreteStopId(stop)
      if (!instancesById.has(id)) {
        instancesById.set(id, new Map())
      }
      instancesById.get(id)?.set(streamId, stop.blue)
    }
  }

  const reconciledStops: ReconciledDiscreteStop[] = Array.from(idToColor, ([id, color]) => ({
    id,
    value: idToValue.get(id),
    color,
    instances: instancesById.get(id) ?? new Map()
  }))

  return { hasConflicts, remappedColors, reconciledStops }
}

/* ---------------------------------- tests --------------------------------- */

if (import.meta.vitest) {
  const { describe, test, expect } = import.meta.vitest

  describe('reconcileDiscreteLegends', () => {
    test('single stream returns unchanged colors and no conflicts', () => {
      const streams = [
        {
          streamId: 'A',
          discreteColors: [
            { blue: 1, color: '#aaa', label: 'Hybrid ABC', type: 'discrete' as const },
            { blue: 2, color: '#bbb', label: 'Hybrid XYZ', type: 'discrete' as const }
          ]
        }
      ]

      const result = reconcileDiscreteLegends(streams)

      expect(result.hasConflicts).toBe(false)
      expect(result.remappedColors.get('A')).toEqual(streams[0].discreteColors)
      expect(result.reconciledStops).toHaveLength(2)
      expect(result.reconciledStops[0]).toMatchObject({ id: 'Hybrid ABC', color: '#aaa' })
    })

    test('two streams with no overlapping blue values have no conflicts', () => {
      const streams = [
        {
          streamId: 'A',
          discreteColors: [
            { blue: 1, color: '#aaa', label: 'Hybrid ABC', type: 'discrete' as const }
          ]
        },
        {
          streamId: 'B',
          discreteColors: [
            { blue: 2, color: '#bbb', label: 'Hybrid DEF', type: 'discrete' as const }
          ]
        }
      ]

      const result = reconcileDiscreteLegends(streams)

      expect(result.hasConflicts).toBe(false)
    })

    test('detects conflict when two streams share a blue value with different labels', () => {
      const streams = [
        {
          streamId: 'A',
          discreteColors: [
            { blue: 1, color: '#aaa', label: 'Hybrid ABC', type: 'discrete' as const }
          ]
        },
        {
          streamId: 'B',
          discreteColors: [
            { blue: 1, color: '#aaa', label: 'Hybrid DEF', type: 'discrete' as const }
          ]
        }
      ]

      const result = reconcileDiscreteLegends(streams)

      expect(result.hasConflicts).toBe(true)
    })

    test('primary stream colors are preserved; conflicting secondary labels get new colors', () => {
      const streams = [
        {
          streamId: 'A',
          discreteColors: [
            { blue: 1, color: '#aaa', label: 'Hybrid ABC', type: 'discrete' as const },
            { blue: 2, color: '#bbb', label: 'Hybrid XYZ', type: 'discrete' as const }
          ]
        },
        {
          streamId: 'B',
          discreteColors: [
            { blue: 1, color: '#aaa', label: 'Hybrid DEF', type: 'discrete' as const },
            { blue: 2, color: '#bbb', label: 'Hybrid ABC', type: 'discrete' as const }
          ]
        }
      ]

      const result = reconcileDiscreteLegends(streams)

      // Primary stream unchanged
      expect(result.remappedColors.get('A')).toEqual(streams[0].discreteColors)

      // Secondary stream: blue=2 (Hybrid ABC) gets primary's color for ABC (#aaa)
      const remappedB = result.remappedColors.get('B')
      expect(remappedB).toBeDefined()
      expect(remappedB?.find((s) => s.blue === 2)?.color).toBe('#aaa')

      // Secondary stream: blue=1 (Hybrid DEF) gets a new color, not #aaa or #bbb
      const defColor = remappedB?.find((s) => s.blue === 1)?.color
      expect(defColor).not.toBe('#aaa')
      expect(defColor).not.toBe('#bbb')
    })

    test('shared label across streams gets the same canonical color', () => {
      const streams = [
        {
          streamId: 'A',
          discreteColors: [
            { blue: 1, color: '#aaa', label: 'Hybrid ABC', type: 'discrete' as const }
          ]
        },
        {
          streamId: 'B',
          discreteColors: [
            { blue: 2, color: '#bbb', label: 'Hybrid ABC', type: 'discrete' as const }
          ]
        }
      ]

      const result = reconcileDiscreteLegends(streams)

      // Both streams should render Hybrid ABC with the same color (#aaa from primary)
      expect(result.remappedColors.get('A')?.[0].color).toBe('#aaa')
      expect(result.remappedColors.get('B')?.[0].color).toBe('#aaa')

      // Unified legend has one entry for Hybrid ABC
      expect(result.reconciledStops).toHaveLength(1)
      expect(result.reconciledStops[0].instances.get('A')).toBe(1)
      expect(result.reconciledStops[0].instances.get('B')).toBe(2)
    })

    test('instances map correctly tracks per-stream blue values', () => {
      const streams = [
        {
          streamId: 'A',
          discreteColors: [
            { blue: 3, color: '#ccc', label: 'Hybrid ABC', type: 'discrete' as const }
          ]
        },
        {
          streamId: 'B',
          discreteColors: [
            { blue: 7, color: '#ccc', label: 'Hybrid ABC', type: 'discrete' as const }
          ]
        }
      ]

      const result = reconcileDiscreteLegends(streams)
      const abcEntry = result.reconciledStops.find((e) => e.id === 'Hybrid ABC')

      expect(abcEntry).toBeDefined()
      expect(abcEntry?.instances.get('A')).toBe(3)
      expect(abcEntry?.instances.get('B')).toBe(7)
    })

    test('value-only stops carry value through for consumer formatting', () => {
      const streams = [
        {
          streamId: 'A',
          discreteColors: [
            { blue: 1, color: '#aaa', value: 1.5, type: 'discrete' as const },
            { blue: 2, color: '#bbb', value: 3.0, type: 'discrete' as const }
          ]
        }
      ]

      const result = reconcileDiscreteLegends(streams)

      expect(result.reconciledStops).toHaveLength(2)
      expect(result.reconciledStops[0].id).toBe('1.5')
      expect(result.reconciledStops[0].value).toBe(1.5)
      expect(result.reconciledStops[1].id).toBe('3')
      expect(result.reconciledStops[1].value).toBe(3.0)
    })

    test('empty streams returns empty result', () => {
      const result = reconcileDiscreteLegends([])

      expect(result.hasConflicts).toBe(false)
      expect(result.reconciledStops).toHaveLength(0)
      expect(result.remappedColors.size).toBe(0)
    })
  })
}
